import { serverSetAccessToken } from "@/api";
import { Button, TextField } from "@/design-system";
import { useChangePassword } from "@/domain";
import { useToast } from "@/hooks";
import { phoneUtils } from "@/utils";
import { consumeLoginRedirect } from "@/utils/loginRedirect";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import {
  changePasswordScheme,
  type ChangePasswordScheme,
} from "./changePasswordScheme";

/**
 * Troca de senha, usada na tela do dashboard e na de autenticação.
 *
 * Os dois campos de senha ligam o botão de mostrar/ocultar: senha errada por
 * uma tecla é o tipo de erro que só aparece depois de enviar, e no celular,
 * digitando de pé, conferir o que se escreveu é o que evita a segunda tentativa.
 */
export const ChangePasswordForm = () => {
  const { formState, control, handleSubmit } = useForm<ChangePasswordScheme>({
    values: {
      phone: "",
      oldPassword: "",
      newPassword: "",
    },
    resolver: zodResolver(changePasswordScheme),
  });

  const { changePassword, isPendingChangePassword } = useChangePassword();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const handleChangePassword = (data: ChangePasswordScheme) => {
    const formattedPhone = phoneUtils.cleanPhoneNumber(data.phone);

    changePassword(
      {
        newPassword: data.newPassword,
        oldPassword: data.oldPassword,
        phone: formattedPhone,
      },
      {
        onSuccess: ({ accessToken, refreshToken, session }) => {
          serverSetAccessToken(accessToken, refreshToken, session);
          navigate(consumeLoginRedirect() ?? "/dashboard");
        },
        onError: () => {
          showToast(
            "Não foi possível alterar a senha. Confira o celular e a senha antiga.",
            "error"
          );
        },
      }
    );
  };

  return (
    <form
      onSubmit={handleSubmit(handleChangePassword)}
      className="flex flex-1 flex-col gap-4"
    >
      <Controller
        control={control}
        name="phone"
        render={({ field, fieldState }) => (
          <TextField
            label="Celular"
            required
            inputMode="tel"
            autoComplete="tel"
            placeholder="(11)98888-7777"
            error={fieldState.error?.message}
            value={field.value}
            onChange={evento =>
              field.onChange(phoneUtils.formatPhone(evento.target.value))
            }
            onBlur={field.onBlur}
          />
        )}
      />

      <Controller
        control={control}
        name="oldPassword"
        render={({ field, fieldState }) => (
          <TextField
            label="Senha antiga"
            required
            type="password"
            passwordToggle
            autoComplete="current-password"
            error={fieldState.error?.message}
            {...field}
          />
        )}
      />

      <Controller
        control={control}
        name="newPassword"
        render={({ field, fieldState }) => (
          <TextField
            label="Nova senha"
            required
            type="password"
            passwordToggle
            autoComplete="new-password"
            error={fieldState.error?.message}
            {...field}
          />
        )}
      />

      <Button
        type="submit"
        disabled={!formState.isValid}
        isLoading={isPendingChangePassword}
        loadingLabel="Alterando a senha"
      >
        Alterar senha
      </Button>
    </form>
  );
};
